import { useTimelineContext } from 'dnd-timeline';
import { Ban } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { placePopover } from '../../../components/ui/popoverPlacement.js';
import { type ScheduleView, mytClock, mytDayLabel } from './dropLogic.js';
import type { ColumnAvailability } from './occupancy.js';

/** How a column names itself: its day in Week and Month, its day and hour in Day. */
function columnName(column: ColumnAvailability, view: ScheduleView): string {
  const day = mytDayLabel(column.start);
  return view === 'day' ? `${day} ${mytClock(column.start)}` : day;
}

function accessibleName(
  column: ColumnAvailability,
  view: ScheduleView,
  namePrefix: string | undefined,
): string {
  const name = namePrefix ? `${namePrefix}, ${columnName(column, view)}` : columnName(column, view);
  if (column.eligible === 0) return `${name}: No cars to count`;
  const counts = `${column.free} of ${column.eligible} cars free`;
  if (column.free === 0) return `${name}: ${counts}`;
  return `${name}: ${counts}: ${column.freeCars.map((c) => `${c.label} ${c.plate}`).join(', ')}`;
}

/**
 * 317 (FR-008 to FR-011), extracted by 319 (R3) — the free-count cells for a set of columns, used by
 * the fleet's free-cars row and by every group heading: the same buttons, the same "0 free" word and
 * mark, the same popover, so a group's count follows the same rule by construction.
 */
export function FreeCells({
  columns,
  view,
  testIdPrefix = 'schedule-free-',
  namePrefix,
}: {
  columns: ColumnAvailability[];
  view: ScheduleView;
  /** `schedule-free-` for the fleet row; a group heading passes its own. */
  testIdPrefix?: string;
  /** 319: a group heading names itself first ("George Town, Sat 26 Sep: …"). */
  namePrefix?: string;
}) {
  const { range, valueToPixels } = useTimelineContext();
  const [open, setOpen] = useState<number | null>(null);
  const cellRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      {columns.map((column, i) => (
        <button
          key={column.start}
          ref={(el) => {
            cellRefs.current[i] = el;
          }}
          type="button"
          data-testid={`${testIdPrefix}${column.start}`}
          data-free={column.free}
          data-eligible={column.eligible}
          data-full={String(column.full)}
          aria-label={accessibleName(column, view, namePrefix)}
          onFocus={() => setOpen(i)}
          onBlur={() => setOpen((cur) => (cur === i ? null : cur))}
          onMouseEnter={() => setOpen(i)}
          onMouseLeave={() => setOpen((cur) => (cur === i ? null : cur))}
          className={[
            '@container absolute top-0 flex h-full items-center justify-center gap-0.5 overflow-hidden border-l border-hairline text-[11px] whitespace-nowrap',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue',
            column.full
              ? 'bg-semantic-warning-bg font-semibold text-semantic-warning-text'
              : 'text-slate',
          ].join(' ')}
          style={{
            left: valueToPixels(column.start - range.start),
            width: valueToPixels(column.end - column.start),
          }}
        >
          {column.full && (
            <Ban data-testid="schedule-free-full-mark" aria-hidden="true" size={11} />
          )}
          {/* A Month column is ~32 px: the number alone fits, and the row's own label says
            "Free cars"; the word appears once the cell has room (and is always in the name). */}
          {column.eligible === 0 ? (
            '—'
          ) : (
            <>
              <span>{column.free}</span>
              <span className="hidden @min-[3.5rem]:inline"> free</span>
            </>
          )}
        </button>
      ))}
      {open !== null && columns[open] && (
        <FreePopover column={columns[open]} anchor={cellRefs.current[open] ?? null} />
      )}
    </>
  );
}

function FreePopover({
  column,
  anchor,
}: {
  column: ColumnAvailability;
  anchor: HTMLElement | null;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!anchor || !box) return;
    const { width, height } = box.getBoundingClientRect();
    setPos(
      placePopover(
        anchor.getBoundingClientRect(),
        { width, height },
        { width: window.innerWidth, height: window.innerHeight },
        4,
      ),
    );
  }, [anchor]);
  return createPortal(
    <div
      ref={boxRef}
      data-testid="schedule-free-popover"
      role="tooltip"
      className="pointer-events-none z-50 max-w-72 rounded-md border border-hairline bg-canvas px-2.5 py-2 text-xs text-ink shadow-lg"
      style={{
        position: 'fixed',
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {column.freeCars.length === 0 ? (
        'No car is free'
      ) : (
        <ul className="flex flex-col gap-0.5">
          {column.freeCars.map((c) => (
            <li key={c.id}>
              {c.label} <span className="text-steel">{c.plate}</span>
            </li>
          ))}
        </ul>
      )}
    </div>,
    document.body,
  );
}
